/*
 * The one button motif in the design: a flat fill sitting on a hard offset
 * shadow that presses down under the cursor. Bordered variants carry 2px of
 * border, so their padding is 2px tighter than the flat ones at the same size.
 */
const VARIANTS = {
  primary:
    'bg-forest text-white shadow-[0_5px_0_var(--color-forest-deep)] hover:translate-y-[3px] hover:shadow-[0_2px_0_var(--color-forest-deep)]',
  coral:
    'bg-coral text-white shadow-[0_3px_0_var(--color-coral-deep)] hover:translate-y-[2px] hover:shadow-[0_1px_0_var(--color-coral-deep)]',
  outline:
    'bg-white text-forest border-2 border-forest shadow-[0_5px_0_rgb(20_83_45_/_0.18)] hover:bg-sun hover:translate-y-[3px] hover:shadow-[0_2px_0_rgb(20_83_45_/_0.18)]',
  outlineFlat: 'text-forest border-2 border-forest hover:bg-sun',
  // The one button that sits on dark green, where a forest fill would vanish.
  sun: 'bg-sun text-forest hover:bg-white',
  outlineFill: 'text-forest border-2 border-forest hover:bg-forest hover:text-cream',
  // Same shell as `outline`, but instead of flooding yellow it grows the coral
  // from the `coral` variant out of the centre. The fill layer does the colour;
  // this only handles the press and the text flipping to white to stay legible.
  outlineSweep:
    'group relative isolate overflow-hidden bg-white text-forest border-2 border-forest shadow-[0_5px_0_rgb(20_83_45_/_0.18)] hover:translate-y-[3px] hover:shadow-[0_2px_0_rgb(20_83_45_/_0.18)]',
};

const BORDERED = new Set(['outline', 'outlineFlat', 'outlineFill', 'outlineSweep']);

const SIZES = {
  sm: {
    flat: 'px-[18px] py-[10px]',
    bordered: 'px-4 py-2',
    type: 'text-sm font-sans font-bold',
  },
  md: {
    flat: 'px-6 py-[13px]',
    bordered: 'px-[22px] py-[11px]',
    type: 'text-[17px] font-display font-bold',
  },
  lg: {
    flat: 'px-[30px] py-[15px]',
    bordered: 'px-[26px] py-[13px]',
    type: 'text-lg font-display font-bold',
  },
};

export default function Button({
  as: Tag = 'a',
  variant = 'primary',
  size = 'lg',
  block = false,
  className = '',
  children,
  ...rest
}) {
  const sizing = SIZES[size];
  const padding = BORDERED.has(variant) ? sizing.bordered : sizing.flat;
  const sweep = variant === 'outlineSweep';

  return (
    <Tag
      className={[
        'inline-flex items-center justify-center gap-2 rounded-full text-center',
        'cursor-pointer transition-[transform,box-shadow,background-color,color] duration-150',
        sizing.type,
        padding,
        VARIANTS[variant],
        block ? 'w-full' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {sweep ? (
        <>
          {/* A circle as wide as the button, grown past its corners. `isolate`
              on the root keeps -z-1 above the white background but below the
              label. */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-1/2 top-1/2 -z-1 aspect-square w-full -translate-x-1/2 -translate-y-1/2 scale-0 rounded-full bg-coral transition-transform duration-500 ease-out group-hover:scale-150 group-focus-visible:scale-150"
          />
          {/* The label flips on its own clock, held back so the coral is
              already under it — otherwise it goes white against white. */}
          <span className="transition-colors delay-150 duration-200 group-hover:text-white group-focus-visible:text-white motion-reduce:delay-0">
            {children}
          </span>
        </>
      ) : (
        children
      )}
    </Tag>
  );
}
